import { type FC, type KeyboardEvent } from 'react'
import './dashboard-banner.css'

import * as Icons from '@icons'
import { priceFormatter } from '@utils/number-helper'
import { flatten } from '@utils/string-helper'


export type DashboardBannerVariant = 'promo' | 'creditAmount' | 'welcome' | 'limitedAccount'

interface Props extends React.HTMLAttributes<HTMLDivElement> {
    variant: DashboardBannerVariant
    creditAmount?: number
    promoAmount?: number
    groupSpendRequirement?: number
    participantCountRequirement?: number
}

const bannerContent: Record<Exclude<DashboardBannerVariant, 'promo'>, { badge: string, badgeLabel: string, eyebrow: string, headline: string, description: string }> = {
    creditAmount: {
        badge: '$5',
        badgeLabel: 'BONUS',
        eyebrow: 'Referral program',
        headline: 'Invite friends. Earn credits.',
        description: 'Share your link and you both earn $5 once their first group is finalized.',
    },
    welcome: {
        badge: 'NEW',
        badgeLabel: 'USER',
        eyebrow: 'Welcome',
        headline: 'Start your first group today.',
        description: 'Create one card, invite your crew, and keep every dollar organized in one place.',
    },
    limitedAccount: {
        badge: 'LIMIT',
        badgeLabel: 'NOTICE',
        eyebrow: 'Account update',
        headline: 'Some actions are currently unavailable.',
        description: 'Tap to review what is limited on your account and how to contact support.',
    },
}

const DashboardBanner: FC<Props> = (props) => {
    const {
        className: classProps,
        variant,
        creditAmount = 0,
        promoAmount = 0,
        groupSpendRequirement = 10000,
        participantCountRequirement = 3,
        onClick,
        onKeyDown,
        ...divProps
    } = props
    const formattedPromoAmount = priceFormatter.format(promoAmount / 100)
    const formattedGroupSpendRequirement = priceFormatter.format(groupSpendRequirement / 100)
    const banner = variant === 'promo'
        ? {
            badge: promoAmount > 0 ? formattedPromoAmount : '$5',
            badgeLabel: 'BONUS',
            eyebrow: 'First group bonus',
            headline: `Earn ${promoAmount > 0 ? formattedPromoAmount : '$5'} on your first group.`,
            description: `Reach ${participantCountRequirement} total people and spend ${formattedGroupSpendRequirement} on the group card to qualify.`,
        }
        : bannerContent[variant]
    const className = flatten('dashboard-banner', onClick ? 'is-clickable' : '', classProps)

    function handleKeyDown(event: KeyboardEvent<HTMLDivElement>) {
        if (!onClick) return
        onKeyDown?.(event)
        if (event.defaultPrevented) return

        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            event.currentTarget.click()
        }
    }

    const formattedAmount = (variant === 'creditAmount' || variant === 'promo') && creditAmount > 0
        ? priceFormatter.format(creditAmount / 100)
        : null

    const badgeAmount = formattedAmount ?? banner.badge

    const headline = variant === 'promo' && formattedAmount
        ? `${formattedAmount} on us - seriously.`
        : banner.headline

    const description = variant === 'promo' && formattedAmount
        ? `We will cover ${formattedAmount} of your first contribution when you create your first group today.`
        : banner.description

    return (
        <div
            className={className}
            data-variant={variant}
            role={onClick ? 'button' : undefined}
            tabIndex={onClick ? 0 : undefined}
            onClick={onClick}
            onKeyDown={handleKeyDown}
            {...divProps}
        >
            <div className='dashboard-banner-badge'>
                {variant === 'limitedAccount'
                    ? <Icons.Lock tint='var(--splitcard-white)' width='1.75rem' height='1.75rem' />
                    : (<>
                        <div className='dashboard-banner-badge-amount'>{badgeAmount}</div>
                        <div className='dashboard-banner-badge-label'>{banner.badgeLabel}</div>
                    </>)
                }
            </div>
            <div className='dashboard-banner-copy'>
                <div className='dashboard-banner-eyebrow'>{banner.eyebrow}</div>
                <div className='dashboard-banner-headline'>{headline}</div>
                <p className='dashboard-banner-description'>{description}</p>
            </div>
        </div>
    )
}

export default DashboardBanner
